import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Field, change as changeFieldValue, formValueSelector } from 'redux-form';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardText, CardTitle, Col, FormGroup, InputGroup, InputGroupAddon, Label, Row } from 'reactstrap';
import { PricingsField, ToggleField } from '../../components';
import CardIconAction from '../CardIconAction/CardIconAction';
import PricingStockImpactModal from '../PricingStockImpactModal';
import RedundantOverridesAlert from '../RedundantOverridesAlert';
import { EMPTY_OBJECT, Tickettypes } from '../../services';
import { pricing_display_names } from '../../services/layoutReferenceUtils';
import { findSharedPricingIdUsages, hasSharedPricingIdUsages } from '../../services/pricingStockImpact';
import { find_redundant_stock_overrides } from '../../services/redundantOverrides';
import { loadTickettypes, addStockOverride, removeStockOverride, renameStockOverride, updatePricingsKeys } from '../../redux/modules/tickettypes';

import './TickettypeStocksForm.css';
import '../CardIconAction/CardIconAction.scss';

const tickettypeFormSelector = formValueSelector('tickettypeForm');

const initial_impact_state = { isOpen: false, pricingId: null, matches: null };

const TickettypeStocksForm = ({ tickettype, pricinglists, isOneTimePass }) => {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const { tickettypes } = useSelector(state => state.tickettypes);
    const stocksFormValues = useSelector(state => (
        tickettypeFormSelector(state, 'rules.stocks') || EMPTY_OBJECT
    ));
    const [overrideIds, setOverrideIds] = useState([]);
    const [persistedOverrideIds, setPersistedOverrideIds] = useState([]);
    const [pricingIdToAdd, setPricingIdToAdd] = useState('');
    const [pricingSelectKey, setPricingSelectKey] = useState(0);
    const [impactState, setImpactState] = useState(initial_impact_state);
    const [isAligning, setIsAligning] = useState(false);

    useEffect(() => {
        dispatch(loadTickettypes());
    }, [dispatch]);

    const stock_override_ids_from_tickettype = useCallback(() => (
        Object.keys(tickettype?.rules?.stocks?.overrides || {})
    ), [tickettype]);

    // Visible overrides follow live tickettype (session adds mutate the store).
    useEffect(() => {
        setOverrideIds(stock_override_ids_from_tickettype());
    }, [stock_override_ids_from_tickettype]);

    // Only IDs present when the tickettype was loaded/switched are "persisted".
    // Session-added overrides must not enter this set until reload after save.
    useEffect(() => {
        setPersistedOverrideIds(stock_override_ids_from_tickettype());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tickettype?._id]);

    const pricingOptions = useMemo(() => {
        if (isOneTimePass) {
            const seen = new Set();
            const options = [];
            (pricinglists || []).forEach(list => {
                Object.keys(list.pricings || {}).forEach(id => {
                    if (overrideIds.includes(id) || seen.has(id))
                        return;
                    seen.add(id);
                    options.push({ id, name: list.pricings[id].name });
                });
            });
            return options;
        }
        return Object.keys(tickettype?.pricings || {})
            .filter(id => !overrideIds.includes(id))
            .map(id => ({ id, name: tickettype.pricings[id].name }));
    }, [isOneTimePass, pricinglists, tickettype, overrideIds]);

    const pricingName = useCallback((id) => {
        const names = pricing_display_names(tickettype, id, i18n.language, isOneTimePass ? pricinglists : null);
        return names.length ? names.join(', ') : id;
    }, [tickettype, pricinglists, isOneTimePass, i18n.language]);

    const clearPricingToAdd = useCallback(() => {
        setPricingIdToAdd('');
        setPricingSelectKey(key => key + 1);
    }, []);

    const addOverride = () => {
        if (!pricingIdToAdd)
            return;
        const id = pricingIdToAdd;
        dispatch(addStockOverride(id));
        setOverrideIds(prev => (prev.includes(id) ? prev : [...prev, id]));
        clearPricingToAdd();
    };

    const removeOverride = (id) => {
        // removeStockOverride syncs rules.stocks.overrides into the form
        // (same keepDirtyOnReinitialize pattern as layouts).
        dispatch(removeStockOverride(id));
        setOverrideIds(prev => prev.filter(v => v !== id));
        // Remount so Semantic does not keep the removed id as a ghost selection.
        clearPricingToAdd();
    };

    const redundantItems = useMemo(() => (
        find_redundant_stock_overrides(stocksFormValues, persistedOverrideIds)
            .filter(id => overrideIds.includes(id))
            .map(id => ({ id, label: pricingName(id), icon: 'fa-tag' }))
    ), [stocksFormValues, overrideIds, persistedOverrideIds, pricingName]);

    const closeImpactModal = () => {
        if (impactState.pricingId) {
            dispatch(changeFieldValue('tickettypeForm', `rules.stocks.overrides.${impactState.pricingId}.independent_of_tickettype`, false));
        }
        setImpactState(initial_impact_state);
    };

    const onIndependentToggle = (pricingId, checked) => {
        if (!checked)
            return;
        const matches = findSharedPricingIdUsages(pricingId, tickettype, tickettypes, pricinglists);
        if (!hasSharedPricingIdUsages(matches))
            return;
        setImpactState({ isOpen: true, pricingId, matches });
    };

    const alignMaxQuantity = async () => {
        const { pricingId, matches } = impactState;
        const currentMaxQuantity = stocksFormValues?.overrides?.[pricingId]?.max_quantity ?? null;
        setIsAligning(true);
        try {
            await Promise.all((matches?.tickettypes || []).map(async (match) => {
                const source = (tickettypes || []).find(tt => String(tt._id) === String(match._id));
                if (!source)
                    return;
                const nextOverrides = {
                    ...(source.rules?.stocks?.overrides || {}),
                    [pricingId]: {
                        ...(source.rules?.stocks?.overrides?.[pricingId] || {}),
                        max_quantity: currentMaxQuantity,
                    },
                };
                await Tickettypes.update({
                    ...source,
                    rules: {
                        ...(source.rules || {}),
                        stocks: {
                            ...(source.rules?.stocks || {}),
                            overrides: nextOverrides,
                        },
                    },
                });
            }));
            dispatch(loadTickettypes());
        } finally {
            setIsAligning(false);
            setImpactState(initial_impact_state);
        }
    };

    const renamePricingId = (newId) => {
        const { pricingId } = impactState;
        if (!newId || newId === pricingId)
            return;
        dispatch(renameStockOverride({ oldId: pricingId, newId }));
        if (!isOneTimePass) {
            dispatch(updatePricingsKeys({ oldId: pricingId, newId }));
        }
        setOverrideIds(prev => prev.map(id => (id === pricingId ? newId : id)));
        setPersistedOverrideIds(prev => prev.map(id => (id === pricingId ? newId : id)));
        setImpactState(initial_impact_state);
    };

    const takenPricingIds = useMemo(() => (
        [
            ...Object.keys(tickettype?.pricings || {}),
            ...overrideIds,
        ]
    ), [tickettype, overrideIds]);

    return (
        <div className="TickettypeStocksForm">
            {isOneTimePass && (
                <div className="alert alert-info alert-inline py-2 px-3 mb-3">
                    <span className="alert-inline-body">
                        {t('tickettypes.stocks.one_time_pass_info')}
                    </span>
                </div>
            )}
            <div className="TickettypeStocksForm-addOverride mb-3">
                <RedundantOverridesAlert
                    items={redundantItems}
                    onRemove={removeOverride}
                />
            </div>
            <Row>
                <Col>
                    <Card className="CardShell mb-3">
                        <CardBody>
                            <CardTitle>
                                <i className="fa fa-battery-three-quarters"></i>&nbsp;
                                {t('tickettypes.stocks.all_pricings_title')}
                            </CardTitle>
                            <CardText tag="div">
                                <FormGroup row>
                                    <Label sm={4}>{t('tickettypes.stocks.max_quantity')}</Label>
                                    <Col sm={8}>
                                        <Field
                                            name="rules.stocks.all_pricings.max_quantity"
                                            component="input"
                                            type="number"
                                            min="1"
                                            step="1"
                                            className="form-control"
                                            placeholder={t('tickettypes.stocks.max_quantity_placeholder')}
                                        />
                                        <div className="alert alert-info alert-inline py-2 px-3 mt-2 mb-0">
                                            <span className="alert-inline-body">
                                                {t('tickettypes.stocks.max_quantity_help')}
                                            </span>
                                        </div>
                                    </Col>
                                </FormGroup>
                            </CardText>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <div className="mb-3">
                <InputGroup className="w-50">
                    <PricingsField
                        key={pricingSelectKey}
                        pricings={pricingOptions}
                        value={pricingIdToAdd || ''}
                        onChange={setPricingIdToAdd}
                    />
                    <InputGroupAddon addonType="append">
                        <Button color="success" disabled={!pricingIdToAdd} onClick={addOverride}>
                            <i className="fa fa-plus"></i>&nbsp;
                            {t('tickettypes.add_override')}
                        </Button>
                    </InputGroupAddon>
                </InputGroup>
            </div>
            <Row>
                {overrideIds.map(id => (
                    <Col sm={12} lg={6} key={id} className="mb-3">
                        <Card className="CardShell h-100">
                            <CardBody>
                                <CardTitle className="d-flex align-items-center justify-content-start w-100 text-left">
                                    <span className="CardIconActions CardIconActions--inline CardIconActions--inline-start mb-0">
                                        <CardIconAction
                                            id={`delete-stock-override-${id}`}
                                            icon="fa-trash"
                                            danger
                                            title={t('tickettypes.stocks.remove_override')}
                                            onClick={() => removeOverride(id)}
                                        />
                                    </span>
                                    <span>{t('tickettypes.stocks.on_pricing', { pricing: pricingName(id) })}</span>
                                </CardTitle>
                                <CardText tag="div">
                                    <FormGroup row>
                                        <Label sm={5}>{t('tickettypes.stocks.max_quantity')}</Label>
                                        <Col sm={7}>
                                            <Field
                                                name={`rules.stocks.overrides.${id}.max_quantity`}
                                                component="input"
                                                type="number"
                                                min="1"
                                                step="1"
                                                className="form-control"
                                                placeholder={t('tickettypes.stocks.max_quantity_placeholder')}
                                            />
                                        </Col>
                                    </FormGroup>
                                    <FormGroup row className="mb-0 align-items-center">
                                        <Label sm={5}>{t('tickettypes.stocks.independent_of_tickettype')}</Label>
                                        <Col sm={7}>
                                            <ToggleField
                                                name={`rules.stocks.overrides.${id}.independent_of_tickettype`}
                                                onSelectChange={(checked) => onIndependentToggle(id, checked)}
                                            />
                                            <div className="alert alert-info alert-inline py-2 px-3 mt-2 mb-0">
                                                <span className="alert-inline-body">
                                                    {t('tickettypes.stocks.independent_of_tickettype_help')}
                                                </span>
                                            </div>
                                        </Col>
                                    </FormGroup>
                                </CardText>
                            </CardBody>
                        </Card>
                    </Col>
                ))}
            </Row>
            <PricingStockImpactModal
                isOpen={impactState.isOpen}
                pricingId={impactState.pricingId}
                matches={impactState.matches}
                currentMaxQuantity={stocksFormValues?.overrides?.[impactState.pricingId]?.max_quantity ?? null}
                isOneTimePass={isOneTimePass}
                takenPricingIds={takenPricingIds}
                isAligning={isAligning}
                onCancel={closeImpactModal}
                onAlign={alignMaxQuantity}
                onRename={renamePricingId}
            />
        </div>
    );
};

export default TickettypeStocksForm;
